import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import OpportunityChart from './OpportunityChart';
import apiService from '../services/apiService';

const CompanyResults = () => {
  const { domainName } = useParams();
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch data from API
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!domainName) {
        setError('Domain name is required');
        setLoading(false);
        return;
      }

      try {
        const data = await apiService.getCompanyPortfolio(domainName);
        console.log('Company data received:', data);
        console.log('Chart data structure:', data.chart_data);
        setCompanyData(data);
        
      } catch (err) {
        console.error('Error fetching company data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [domainName]);

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <h2>Loading Company Portfolio...</h2>
          <p>Please wait while we load {domainName}'s AI opportunity portfolio.</p>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid #f3f3f3', 
              borderTop: '4px solid #0066cc', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <h2>Error Loading Company Portfolio</h2>
          <p style={{ color: '#dc3545', marginBottom: '20px' }}>{error}</p>
          <div>
            <Link to="/" className="btn btn-primary" style={{ marginRight: '10px' }}>
              Take New Assessment
            </Link>
            <button 
              className="btn btn-secondary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!companyData) {
    return (
      <div className="container">
        <div className="card">
          <h2>No Data Available</h2>
          <p>No company portfolio data found for {domainName}.</p>
          <Link to="/" className="btn btn-primary">
            Start Assessment
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ backgroundColor: 'white', minHeight: '100vh' }}>
      <div className="header" style={{
        background: '#111',
        color: 'white',
        padding: '40px 30px 30px 30px',
        borderRadius: '16px',
        boxShadow: '0 6px 24px rgba(0,0,0,0.20)',
        marginBottom: '32px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <h1 style={{
          fontSize: '2.8em',
          fontWeight: '800',
          letterSpacing: '1px',
          marginBottom: '12px',
          textShadow: '0 2px 8px rgba(255,255,255,0.10)'
        }}>{domainName} <span style={{ color: 'white', fontWeight: '900' }}>– AI Opportunity Portfolio</span></h1>
        <p style={{
          fontSize: '1.3em',
          fontWeight: '500',
          marginBottom: '0',
          color: 'white',
          textShadow: '0 1px 4px rgba(255,255,255,0.08)'
        }}>Strategic Overview of AI Initiatives</p>
      </div>

      {/* Company Summary */}
      <div className="card">
        <h2>Portfolio Summary</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ color: '#0066cc', margin: '0 0 10px 0', fontSize: '2em' }}>
              {companyData.company_info.total_opportunities}
            </h3>
            <p style={{ margin: 0, fontWeight: '600' }}>Total AI Opportunities</p>
          </div>
          
          <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ color: '#28a745', margin: '0 0 10px 0', fontSize: '1.5em' }}>
              {domainName}
            </h3>
            <p style={{ margin: 0, fontWeight: '600' }}>Organization</p>
          </div>
        </div>

        <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#e8f4fd', borderRadius: '8px', border: '1px solid #0066cc' }}>
          <h4 style={{ color: '#0066cc', marginTop: 0 }}>📊 About This Portfolio</h4>
          <p style={{ margin: '10px 0', lineHeight: '1.6' }}>
            This interactive chart shows all AI opportunity assessments submitted by {domainName} team members. 
            Each bubble represents a unique AI opportunity, positioned based on its technical feasibility (Y-axis) 
            and ROI potential (X-axis), with bubble size indicating strategic value.
          </p>
          <p style={{ margin: '10px 0', lineHeight: '1.6', fontWeight: '600' }}>
            Use this visualization to identify quick wins, strategic challenges, and prioritize your AI initiatives 
            based on comprehensive assessment data.
          </p>
        </div>
      </div>

      {/* Chart Visualization */}
      <div className="card">
        <h2>Strategic Assessment Matrix</h2>
        <div 
          id="company-portfolio-chart"
          style={{ 
            minHeight: '700px', 
            position: 'relative',
            backgroundColor: '#ffffff',
            padding: '20px',
            border: '1px solid #e1e5e9',
            borderRadius: '8px',
            marginBottom: '20px',
            overflow: 'visible'
          }}
        >
          {companyData && (
            <OpportunityChart 
              key={`company-chart-${domainName}`}
              companyDomain={domainName}
              isPortfolio={true}
              isPublic={true}
              preloadedData={companyData.chart_data}
              isResultsView={true}
            />
          )}
        </div>
      </div>

      {/* Action Section */}
      <div className="card" style={{ textAlign: 'center', padding: '30px' }}>
        <h3>Ready to Contribute?</h3>
        <p style={{ color: '#666', marginBottom: '30px' }}>
          Have an AI opportunity idea for {domainName}? Add your assessment to the portfolio.
        </p>
        
        <Link 
          to="/"
          style={{
            backgroundColor: '#0066cc',
            color: 'white',
            border: 'none',
            padding: '16px 32px',
            fontSize: '18px',
            fontWeight: 'bold',
            borderRadius: '8px',
            textDecoration: 'none',
            minWidth: '250px',
            minHeight: '60px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#0052a3';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#0066cc';
            e.target.style.transform = 'translateY(0px)';
            e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Submit New AI Opportunity Assessment
        </Link>
        
     
      </div>

      {/* Add CSS for the loading animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default CompanyResults;